// src/pages/Careers.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Star,
  ChefHat,
  Shirt,
  TrendingUp,
  Plus,
  Minus,
  Heart,
  Flame,
  Users,
  Trophy,
  Crown,
  Medal,
  Mail,
  Phone,
  Check,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ──────────────────────────────────────────────────────────────
   CONFIG (WEBHOOK)
────────────────────────────────────────────────────────────── */
const MAKE_CAREERS_WEBHOOK_URL = "https://hook.eu2.make.com/yqnf1fxljl9u1ant12rgk50jseqzj3qe"; 
/* ──────────────────────────────────────────────────────────────
   STORYTELLING DATA
────────────────────────────────────────────────────────────── */
const VALUES = [
  {
    title: "Respect The Craft",
    subtitle: "Technique First",
    desc:
      "We don't take shortcuts. The dough rests for 72 hours. The oven is 485°C. Every basil leaf is placed with intention. If you don't care about the details, you won't last here.",
    icon: Flame,
  },
  {
    title: "Pass The Ball",
    subtitle: "Zero Ego",
    desc:
      "A kitchen is a team sport. When the tickets pile up and the pressure hits, we don't crumble — we communicate. We cover each other's backs. No superstars, just the squad.",
    icon: Users,
  },
  {
    title: "Play With Heart",
    subtitle: "Italian Soul",
    desc:
      "We are serving happiness. Even when your legs are tired in the 90th minute, you smile. Because that energy transfers to the pizza, and then to the guest.",
    icon: Heart,
  },
];

const CAREER_STEPS = [
  {
    step: "01",
    title: "The Debut",
    subtitle: "Rookie Season",
    desc:
      "You join the squad. You learn the D10 system. You get your kit. Whether you start in prep or service, this is about proving your work ethic and soaking up knowledge.",
    icon: Medal,
  },
  {
    step: "02",
    title: "First Team",
    subtitle: "Regular Starter",
    desc:
      "You’ve got the fundamentals down. You can handle volume, stay calm, and help others level up. You’re trusted in the key moments.",
    icon: Trophy,
  },
  {
    step: "03",
    title: "Captaincy",
    subtitle: "Club Legend",
    desc:
      "Management material. You run the shift, protect the quality, and lead the team. We invest in your leadership and give you real responsibility.",
    icon: Crown,
  },
];

// salary removed
const POSITIONS = [
  {
    id: "pizzaiolo",
    role: "Pizzaiolo (The Striker)",
    type: "Full Time",
    desc:
      "You stand in front of the beast (the oven). You control the flow. You need ice in your veins and fire in your hands.",
    reqs: ["Advanced dough knowledge", "Oven management", "High volume experience"],
  },
  {
    id: "waiter",
    role: "Service (The Playmaker)",
    type: "Full Time",
    desc:
      "You read the game. You know what the guest needs before they ask. You can carry a vibe and keep it smooth.",
    // ✅ changed from “wine list” to pizza-related
    reqs: ["English fluency", "Menu confidence (pizza & ingredients)", "Multi-tasking"],
  },
  {
    id: "runner",
    role: "Food Runner (The Winger)",
    type: "Seasonal",
    desc:
      "Pure speed. You ensure the pizza hits the table exactly as intended: hot, fresh, perfect.",
    reqs: ["Agility", "Communication", "Hustle"],
  },
  {
    id: "prep",
    role: "Commis (The Defense)",
    type: "Part Time",
    desc:
      "The foundation. If prep isn't done, the team loses. You are organized, clean, and disciplined.",
    reqs: ["Knife skills", "Hygiene", "Reliability"],
  },

 {
  id: "other",
  role: "Free Agent (Open Application)",
  type: "Part Time",
  desc:
    "Not seeing your position listed? Send an open application. If you’re solid, reliable, and you genuinely care about the craft, we’ll find the right place for you in the squad — kitchen, prep, service, or matchday support.",
  reqs: ["Strong work ethic", "Clean & organized", "Fast learner"],
},
];

/* ──────────────────────────────────────────────────────────────
   ANIMATION
────────────────────────────────────────────────────────────── */
const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ y: 40, opacity: 0 }}
    whileInView={{ y: 0, opacity: 1 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

/* ──────────────────────────────────────────────────────────────
   HERO
────────────────────────────────────────────────────────────── */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div
      ref={ref}
      className="relative h-[85vh] min-h-[600px] bg-[#0087E6] overflow-hidden flex flex-col justify-center items-center px-4 text-center"
    >
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src="/img/team/team-normal.png"
          alt="D10 Squad"
          className="w-full h-full object-cover mix-blend-overlay opacity-90"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0087E6]/80 via-[#0087E6]/60 to-[#0087E6]" />
      </motion.div>

      <div className="relative z-10 max-w-4xl mx-auto">
        <Reveal>
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-6 py-2 rounded-full border border-white/30 mb-8">
            <Shirt size={16} className="text-white" />
            <span className="font-manrope font-bold text-white uppercase text-xs tracking-[0.2em]">
              Wear The Shirt
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <h1 className="font-anton text-[12vw] sm:text-[8vw] md:text-7xl leading-[0.9] text-white uppercase mb-8 drop-shadow-xl">
            This Is Not
            <br /> Just A Job.
          </h1>
        </Reveal>

        <Reveal delay={0.4}>
          <p className="font-serif italic text-white/90 text-xl sm:text-2xl md:text-3xl max-w-2xl mx-auto leading-relaxed mb-10">
            "It's 90 minutes of intensity, passion, and teamwork. Are you ready to play for the badge?"
          </p>
        </Reveal>

        <Reveal delay={0.6}>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => scrollTo("manifesto")}
              className="bg-white text-[#0087E6] font-anton uppercase text-lg py-4 px-10 rounded-xl shadow-xl hover:bg-[#002B5C] hover:text-white transition-all duration-300"
            >
              Read Our Code
            </button>
            <button
              onClick={() => scrollTo("jobs")}
              className="bg-[#002B5C] text-white font-anton uppercase text-lg py-4 px-10 rounded-xl shadow-xl hover:bg-white hover:text-[#002B5C] transition-all duration-300 flex items-center justify-center gap-2"
            >
              See Open Roles <ArrowRight size={18} />
            </button>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────
   MANIFESTO
────────────────────────────────────────────────────────────── */
const Manifesto = () => {
  return (
    <section id="manifesto" className="py-24 bg-white text-[#002B5C] overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <Reveal>
            <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
              The Philosophy
            </span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase mt-4 mb-6">
              The D10 Code
            </h2>
            <p className="font-manrope text-gray-500 text-lg">
              We are not a normal pizzeria. We are inspired by the obsession of Naples and the spirit of a football stadium.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={i * 0.2}>
              <div className="group text-center">
                <div className="w-20 h-20 mx-auto bg-[#F0F9FF] rounded-full flex items-center justify-center mb-6 group-hover:bg-[#0087E6] transition-colors duration-500">
                  <v.icon
                    size={32}
                    className="text-[#0087E6] group-hover:text-white transition-colors duration-500"
                  />
                </div>
                <h3 className="font-anton text-2xl uppercase mb-2">{v.title}</h3>
                <p className="font-serif italic text-[#0087E6] mb-4 text-lg">{v.subtitle}</p>
                <p className="font-manrope text-gray-500 leading-relaxed text-sm">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   CAREER LADDER
────────────────────────────────────────────────────────────── */
const CareerLadder = () => {
  return (
    <section className="py-24 bg-[#002B5C] text-white relative overflow-hidden">
      <div className="absolute bottom-0 left-0 w-full h-1/2 bg-gradient-to-t from-[#0087E6]/20 to-transparent pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <Reveal>
            <span className="text-[#0087E6] font-bold tracking-[0.2em] uppercase text-xs bg-white/10 px-4 py-2 rounded-full">
              Growth & Progression
            </span>
            <h2 className="font-anton text-5xl md:text-6xl uppercase mt-6 mb-4">
              Path To Glory
            </h2>
            <p className="font-manrope text-gray-400 text-lg">
              We don’t offer dead-end jobs. We offer a career.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 relative">
          <div className="hidden lg:block absolute top-12 left-[20%] right-[20%] h-0.5 bg-gradient-to-r from-[#0087E6]/0 via-[#0087E6] to-[#0087E6]/0 border-t-2 border-dashed border-[#0087E6]/50 z-0" />

          {CAREER_STEPS.map((step, i) => (
            <Reveal key={i} delay={i * 0.2}>
              <div className="relative z-10 bg-[#0F172A] border border-white/5 p-8 rounded-2xl hover:border-[#0087E6] transition-all duration-500 group h-full">
                <div className="flex items-center justify-between mb-6">
                  <span className="font-anton text-6xl text-white/5 group-hover:text-[#0087E6]/20 transition-colors">
                    {step.step}
                  </span>
                  <div className="w-14 h-14 bg-[#0087E6] rounded-full flex items-center justify-center shadow-lg shadow-blue-900/50 group-hover:scale-110 transition-transform">
                    <step.icon className="text-white" size={24} />
                  </div>
                </div>

                <h3 className="font-anton text-3xl uppercase mb-1">{step.title}</h3>
                <p className="font-serif italic text-[#0087E6] mb-4">{step.subtitle}</p>
                <p className="font-manrope text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   JOB BOARD
────────────────────────────────────────────────────────────── */
const JobIcon = ({ id }) => {
  if (id === "pizzaiolo") return <Flame size={20} />;
  if (id === "waiter") return <Users size={20} />;
  if (id === "runner") return <TrendingUp size={20} />;
  return <ChefHat size={20} />;
};

const JobBoard = ({ onApply }) => {
  const [active, setActive] = useState(null);
  const toggle = (id) => setActive(active === id ? null : id);

  return (
    <section id="jobs" className="py-24 bg-[#F0F9FF]">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
            Transfer Market
          </span>
          <h2 className="font-anton text-5xl text-[#002B5C] uppercase mt-4">
            Open Positions
          </h2>
          <p className="text-gray-500 font-manrope mt-4">
            Select a role to view details.
          </p>
        </div>

        <div className="space-y-6">
          {POSITIONS.map((job) => (
            <Reveal key={job.id}>
              <motion.div
                className={`bg-white rounded-2xl border-2 transition-all overflow-hidden ${
                  active === job.id
                    ? "border-[#0087E6] shadow-xl"
                    : "border-transparent shadow-sm hover:border-blue-200"
                }`}
              >
                <button
                  onClick={() => toggle(job.id)}
                  className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-6 sm:p-8 text-left gap-4"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                        active === job.id
                          ? "bg-[#0087E6] text-white"
                          : "bg-[#F0F9FF] text-[#0087E6]"
                      }`}
                    >
                      <JobIcon id={job.id} />
                    </div>

                    <div>
                      <h3 className="font-anton text-2xl uppercase text-[#002B5C]">
                        {job.role}
                      </h3>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        {job.type}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                      active === job.id
                        ? "border-[#0087E6] bg-[#0087E6] text-white rotate-180"
                        : "border-gray-200 text-gray-400"
                    }`}
                  >
                    <Minus size={20} className={active === job.id ? "" : "hidden"} />
                    <Plus size={20} className={active === job.id ? "hidden" : ""} />
                  </div>
                </button>

                <AnimatePresence>
                  {active === job.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="px-8 pb-8 pt-0 sm:pl-24 border-t border-gray-50">
                        <p className="font-manrope text-gray-600 text-lg leading-relaxed py-6">
                          {job.desc}
                        </p>

                        <div className="grid sm:grid-cols-2 gap-6 mb-8">
                          <div>
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-3">
                              Requirements
                            </h4>
                            <ul className="space-y-2">
                              {job.reqs.map((r, i) => (
                                <li
                                  key={i}
                                  className="flex items-center gap-2 text-sm font-bold text-[#002B5C]"
                                >
                                  <div className="w-1.5 h-1.5 bg-[#0087E6] rounded-full" />
                                  {r}
                                </li>
                              ))}
                            </ul>
                          </div>

                          <div className="bg-[#F0F9FF] p-4 rounded-xl">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-[#0087E6] mb-2">
                              The Perks
                            </h4>
                            <p className="text-xs text-gray-500 font-manrope">
                              Family meal, tips structure, uniform, and Academy training access.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {["Training", "Team culture", "High standards", "Real growth"].map((p) => (
                                <span
                                  key={p}
                                  className="text-[10px] font-bold uppercase tracking-widest bg-white text-[#002B5C] px-2.5 py-1 rounded-full border border-blue-100"
                                >
                                  {p}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={() => onApply(job.role)}
                          className="w-full py-4 bg-[#002B5C] text-white font-anton uppercase tracking-widest rounded-xl hover:bg-[#0087E6] transition-colors shadow-lg flex items-center justify-center gap-2"
                        >
                          Apply Now <ArrowRight size={18} />
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   ACADEMY TEASER
────────────────────────────────────────────────────────────── */
const AcademyTeaser = () => {
  return (
    <section className="bg-[#0087E6] py-20 relative overflow-hidden text-center">
      <div className="container mx-auto px-4 relative z-10">
        <Reveal>
          <div className="inline-block p-4 rounded-full bg-white/20 backdrop-blur-md mb-6">
            <Star className="text-white fill-white" size={32} />
          </div>

          <h2 className="font-anton text-4xl sm:text-6xl text-white uppercase mb-6">
            No Experience?
            <br />
            No Problem.
          </h2>

          <p className="font-manrope text-white/90 text-xl max-w-2xl mx-auto mb-10">
            Talent can be taught. If you have the passion but lack the skills, join our{" "}
            <strong>D10 Academy</strong>. We turn rookies into pros fast.
          </p>

          <Link
            to="/academy"
            className="inline-flex items-center gap-2 bg-white text-[#0087E6] font-anton uppercase text-lg px-10 py-5 rounded-full hover:bg-[#002B5C] hover:text-white transition-all shadow-2xl"
          >
            Go To Training Camp <ArrowRight size={18} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   APPLICATION FORM (WEBHOOK) — no CV upload, no visible errors
────────────────────────────────────────────────────────────── */
const ApplicationForm = ({ targetJob }) => {
  const roles = useMemo(() => POSITIONS.map((p) => p.role), []);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    role: targetJob || "",
    message: "",
  });

  // honeypot (anti-spam)
  const [company, setCompany] = useState("");

  // UX states: success + sending only (no visible error)
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  // keep a silent error for dev (optional)
  const [silentError, setSilentError] = useState("");

  useEffect(() => {
    if (targetJob) setForm((prev) => ({ ...prev, role: targetJob }));
  }, [targetJob]);

  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const submit = async (e) => {
    e.preventDefault();
    setSuccess(false);
    setSilentError("");

    // Honeypot triggered -> silently succeed
    if (company) {
      setSuccess(true);
      return;
    }

    // If webhook is not set, do NOT show it to users (only silent)
    if (!MAKE_CAREERS_WEBHOOK_URL || MAKE_CAREERS_WEBHOOK_URL.includes("XXXXXXXX")) {
      setSilentError("Webhook URL missing.");
      // still show a generic success-ish flow? better: do nothing + keep button enabled
      return;
    }

    // basic required
    if (!form.name.trim() || !form.email.trim() || !form.role) return;

    setSending(true);

    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        role: form.role,
        message: form.message.trim(),
        source: "D10 Careers",
        page: typeof window !== "undefined" ? window.location.href : "",
        submittedAt: new Date().toISOString(),
      };

      const res = await fetch(MAKE_CAREERS_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Webhook failed (${res.status}). ${text || ""}`.trim());
      }

      setSuccess(true);
      setForm({ name: "", phone: "", email: "", role: targetJob || "", message: "" });
    } catch (err) {
      // ✅ do not show error to end user
      setSilentError(err?.message || "Unknown error");
      // optional: you can console.log for debugging
      // eslint-disable-next-line no-console
      console.error("Careers webhook error:", err);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contract" className="py-24 bg-white">
      <div className="container mx-auto px-4 max-w-3xl">
        <Reveal>
          <div className="bg-white rounded-3xl p-8 md:p-16 border-2 border-gray-100 shadow-[0_20px_60px_rgba(0,43,92,0.1)] relative overflow-hidden">
            {/* Decor */}
            <div className="absolute top-0 right-0 bg-[#002B5C] w-32 h-32 rounded-bl-[100px] z-0" />
            <div className="absolute top-8 right-8 text-white z-10 font-anton text-xl opacity-50">
              D10
            </div>

            <div className="relative z-10">
              <span className="text-[#0087E6] font-bold uppercase tracking-widest text-xs">
                Final Step
              </span>
              <h2 className="font-anton text-5xl text-[#002B5C] uppercase mt-2 mb-6">
                Send Your Application
              </h2>

              <p className="font-manrope text-gray-500 mb-10">
                We read everything. Keep it honest, simple, and specific.
              </p>

              {success && (
                <div className="mb-8 rounded-2xl border border-[#00D4AA]/30 bg-[#00D4AA]/10 p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#00D4AA]/20 flex items-center justify-center flex-shrink-0">
                      <Check className="text-[#00D4AA]" />
                    </div>
                    <div>
                      <p className="font-bold text-[#002B5C]">Application sent.</p>
                      <p className="text-sm text-gray-500">We’ll get back to you soon.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={submit} className="space-y-6">
                {/* honeypot */}
                <input
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      className="w-full bg-gray-50 border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all"
                      placeholder="Diego Maradona"
                      disabled={sending}
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className="w-full bg-gray-50 border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all"
                      placeholder="+385 99..."
                      disabled={sending}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    className="w-full bg-gray-50 border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope font-bold text-[#002B5C] outline-none transition-all"
                    placeholder="you@email.com"
                    disabled={sending}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    Target Position *
                  </label>
                  <select
                    required
                    value={form.role}
                    onChange={(e) => setField("role", e.target.value)}
                    className="w-full bg-[#002B5C] text-white border-none rounded-xl px-4 py-4 outline-none font-anton uppercase tracking-wider text-lg cursor-pointer"
                    disabled={sending}
                  >
                    <option value="" disabled>
                      Select Role...
                    </option>
                    {roles.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                    The Pitch (Why you?)
                  </label>
                  <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    className="w-full bg-gray-50 border-2 border-transparent focus:bg-white focus:border-[#0087E6] rounded-xl px-4 py-3 font-manrope text-[#002B5C] outline-none transition-all resize-none"
                    placeholder="Tell us about your game..."
                    disabled={sending}
                  />
                </div>

                <button
                  type="submit"
                  disabled={sending}
                  className={`w-full py-5 font-anton uppercase text-xl rounded-xl shadow-xl transition-colors mt-2 flex items-center justify-center gap-3
                    ${
                      sending
                        ? "bg-[#0087E6]/60 text-white/80 cursor-not-allowed"
                        : "bg-[#0087E6] text-white hover:bg-[#002B5C]"
                    }`}
                >
                  {sending ? "Sending..." : "Send Application"}
                  <ArrowRight size={20} />
                </button>

                <div className="pt-6 border-t border-gray-100 text-center">
                  <p className="text-xs text-gray-400 mb-3">Prefer to reach out directly?</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
                    <a
                      href="mailto:d10spizzafantasista@gmail.com"
                      className="inline-flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors font-bold"
                    >
                      <Mail size={16} />
                      d10spizzafantasista@gmail.com
                    </a>
                    <span className="hidden sm:block text-gray-300">|</span>
                    <a
                      href="tel:+385955011065"
                      className="inline-flex items-center gap-2 text-[#0087E6] hover:text-[#002B5C] transition-colors font-bold"
                    >
                      <Phone size={16} />
                      +385 95 501 1065
                    </a>
                  </div>

                  {/* dev-only hint (hidden from users) */}
                  <span className="sr-only">{silentError}</span>
                </div>
              </form>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ──────────────────────────────────────────────────────────────
   PAGE
────────────────────────────────────────────────────────────── */
export default function CareersPage() {
  const [selected, setSelected] = useState("");

  const handleApply = (role) => {
    setSelected(role);
    setTimeout(() => {
      document.getElementById("contract")?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  return (
    <div className="bg-white min-h-screen selection:bg-[#0087E6] selection:text-white overflow-x-hidden">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
      `}</style>

      <Navbar />

      <main>
        <Hero />
        <Manifesto />
        <CareerLadder />
        <JobBoard onApply={handleApply} />
        <AcademyTeaser />
        <ApplicationForm targetJob={selected} />
      </main>

      <Footer />
    </div>
  );
}
